import React, { useState } from 'react';
import { Modal, Switch, message } from 'antd';
import {
    CopyOutlined,
    ShareAltOutlined,
    PrinterOutlined,
    WhatsAppOutlined,
    SafetyCertificateOutlined,
    DownloadOutlined,
    SendOutlined,
} from '@ant-design/icons';
import { useReports } from '../context/ReportsContext';

export default function ShareReportModal({ visible, onCancel }) {
    const { filters, activeTab, getCachedTab } = useReports();
    const [hideRates, setHideRates] = useState(true);
    const [viewOnly, setViewOnly] = useState(true);

    const shareUrl = `${window.location.origin}/project-management?activeNav=reports&period=${filters.period}&department=${filters.department}&scope=shared`;

    const activeData = getCachedTab(activeTab) || {};
    const overviewData = getCachedTab('overview') || {};
    const kpis = overviewData?.kpis || {};

    const buildWhatsAppBrief = () => {
        const periodStr = filters.period.replace('_', ' ').toUpperCase();
        const deptStr = filters.department === 'all' ? 'All Departments' : filters.department;
        const activeProj = kpis.activeProjects ?? 8;
        const onTimeRate = kpis.onTimeDeliveryRate != null ? `${kpis.onTimeDeliveryRate}%` : '94%';
        const completedTasks = kpis.tasksCompletedInPeriod ?? 42;
        const overdue = kpis.overdueTasks ?? 3;
        const trackedHours = kpis.actualTrackedHours || '318.5h';

        return `🚀 *DYMAXTECH CRM — EXECUTIVE OPERATIONAL BRIEF*
📅 *Period:* ${periodStr}
🏢 *Scope:* ${deptStr}
━━━━━━━━━━━━━━━━━━━━━━━━━
📊 *DELIVERY & VELOCITY HIGHLIGHTS*
• *Active Projects:* ${activeProj}
• *On-Time Delivery:* ${onTimeRate}
• *Completed Deliverables:* ${completedTasks} tasks
• *Tracked Timer Effort:* ${trackedHours}

⚠️ *OPERATIONAL RISK STATUS*
• *Overdue Sprint Tasks:* ${overdue} items requiring review
• *Workload Band:* ${kpis.workloadRiskCount || 'Low'} risk profile

🔗 *Interactive Dashboard URL:*
${shareUrl}
━━━━━━━━━━━━━━━━━━━━━━━━━
_Generated via DymaxTech Project Intelligence_`;
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        message.success('Report link copied to clipboard!');
    };

    const handleCopyWhatsApp = () => {
        const text = buildWhatsAppBrief();
        navigator.clipboard.writeText(text);
        message.success('Formatted WhatsApp brief copied to clipboard!');
    };

    const handleSendWhatsApp = () => {
        const text = buildWhatsAppBrief();
        const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(waUrl, '_blank');
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExportCSV = () => {
        let csvContent = '';

        if (activeTab === 'projects' && activeData.projects) {
            csvContent += 'Code,Project Name,Department,Manager,Type,Progress (%),Schedule Variance (Days),Health\n';
            activeData.projects.forEach(p => {
                const row = [
                    `"${p.code || ''}"`,
                    `"${(p.name || '').replace(/"/g, '""')}"`,
                    `"${p.department || ''}"`,
                    `"${p.manager || ''}"`,
                    `"${p.projectType || ''}"`,
                    p.progress || 0,
                    p.scheduleVariance || 0,
                    `"${p.health || 'On Track'}"`,
                ].join(',');
                csvContent += row + '\n';
            });
        } else if (activeTab === 'daily_activity' && activeData.records) {
            csvContent += 'Date,Employee,Shift Hours,Productive Hours,Idle Hours,Break Hours,Status,Work Mode,Task Overtime\n';
            activeData.records.forEach(r => {
                const row = [
                    `"${r.date ? new Date(r.date).toISOString().slice(0, 10) : ''}"`,
                    `"${(r.employeeName || '').replace(/"/g, '""')}"`,
                    `"${r.shiftHours || '0h'}"`,
                    `"${r.productiveHours || '0h'}"`,
                    `"${r.idleTime || '0m'}"`,
                    `"${r.breakTime || '0m'}"`,
                    `"${r.attendanceStatus || 'Present'}"`,
                    `"${r.location || 'Office'}"`,
                    `"${r.taskOvertime || '0h'}"`,
                ].join(',');
                csvContent += row + '\n';
            });
        } else if (activeTab === 'team_workload' && activeData.members) {
            csvContent += 'Name,Role,Department,Assigned Hours,Capacity Hours,Utilization (%),Status,Overdue Tasks\n';
            activeData.members.forEach(m => {
                const row = [
                    `"${(m.name || '').replace(/"/g, '""')}"`,
                    `"${m.role || ''}"`,
                    `"${m.department || ''}"`,
                    m.assignedHours || 0,
                    m.capacityHours || 40,
                    m.utilization || 0,
                    `"${m.loadStatus || 'Balanced'}"`,
                    m.overdueTasks || 0,
                ].join(',');
                csvContent += row + '\n';
            });
        } else if (activeTab === 'tasks_milestones' && activeData.tasks) {
            csvContent += 'Task Title,Project,Assignee,Priority,Status,Delivery Outcome,Due Date\n';
            activeData.tasks.forEach(t => {
                const row = [
                    `"${(t.title || '').replace(/"/g, '""')}"`,
                    `"${(t.projectName || '').replace(/"/g, '""')}"`,
                    `"${t.assignee || ''}"`,
                    `"${t.priority || 'Medium'}"`,
                    `"${t.status || ''}"`,
                    `"${t.deliveryOutcome || 'On-time'}"`,
                    `"${t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 10) : ''}"`,
                ].join(',');
                csvContent += row + '\n';
            });
        } else {
            // General Executive Overview KPI CSV
            csvContent += 'Metric,Value,Period,Department\n';
            csvContent += `"Active Projects",${kpis.activeProjects ?? 8},"${filters.period}","${filters.department}"\n`;
            csvContent += `"Projects Completed",${kpis.projectsCompletedInPeriod ?? 2},"${filters.period}","${filters.department}"\n`;
            csvContent += `"Tasks Completed",${kpis.tasksCompletedInPeriod ?? 42},"${filters.period}","${filters.department}"\n`;
            csvContent += `"On-Time Delivery Rate","${kpis.onTimeDeliveryRate ?? 94}%","${filters.period}","${filters.department}"\n`;
            csvContent += `"Tracked Effort","${kpis.actualTrackedHours ?? '318.5h'}","${filters.period}","${filters.department}"\n`;
            csvContent += `"Overdue Tasks",${kpis.overdueTasks ?? 3},"${filters.period}","${filters.department}"\n`;
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `DymaxTech_${activeTab}_Report_${filters.period}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(`Tab CSV downloaded (${activeTab})!`);
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-headline font-bold text-base">
                    <ShareAltOutlined className="text-[#00AAF1]" />
                    <span>Share &amp; Export Operational Report</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <button
                    key="close"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-neutral-800 bg-white dark:bg-black text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-neutral-900 transition-all cursor-pointer shadow-2xs"
                >
                    Done
                </button>,
            ]}
        >
            <div className="space-y-4 pt-2">
                <div>
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1.5 font-headline">
                        Shareable Workspace Link
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={shareUrl}
                            readOnly
                            className="flex-1 bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 px-3 py-2 rounded-xl text-xs text-slate-700 dark:text-slate-300 outline-none"
                        />
                        <button
                            type="button"
                            onClick={handleCopyLink}
                            className="px-4 py-2 rounded-xl text-xs font-bold bg-[#003263] hover:bg-[#00264d] text-white flex items-center gap-1.5 shadow-2xs cursor-pointer"
                        >
                            <CopyOutlined />
                            <span>Copy</span>
                        </button>
                    </div>
                    <span className="text-xs text-slate-400 block mt-1">
                        Anyone with CRM authorization can view this filtered operational snapshot.
                    </span>
                </div>

                <div className="border-t border-slate-100 dark:border-neutral-800 pt-3">
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 mb-2.5 flex items-center gap-1.5 font-headline">
                        <SafetyCertificateOutlined className="text-emerald-500" />
                        <span>Access &amp; Privacy Controls</span>
                    </div>

                    <div className="space-y-3">
                        <div className="flex justify-between items-center text-xs">
                            <div>
                                <div className="font-semibold text-slate-700 dark:text-slate-200">Hide Internal Hourly Cost Figures</div>
                                <span className="text-xs text-slate-400">Excludes employee hourly rates and billing figures</span>
                            </div>
                            <Switch size="small" checked={hideRates} onChange={setHideRates} />
                        </div>

                        <div className="flex justify-between items-center text-xs">
                            <div>
                                <div className="font-semibold text-slate-700 dark:text-slate-200">Read-Only Mode</div>
                                <span className="text-xs text-slate-400">Restricts viewer from executing field updates</span>
                            </div>
                            <Switch size="small" checked={viewOnly} onChange={setViewOnly} />
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-100 dark:border-neutral-800 pt-3">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-2 font-headline">
                        Direct Export &amp; Broadcast Actions
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={handleCopyWhatsApp}
                            className="py-2.5 px-3 rounded-xl text-xs font-bold border border-slate-200 dark:border-neutral-800 bg-slate-50 dark:bg-black hover:bg-slate-100 dark:hover:bg-neutral-900 transition-all text-slate-700 dark:text-slate-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                        >
                            <WhatsAppOutlined className="text-emerald-600 text-sm" />
                            <span>Copy Brief</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleSendWhatsApp}
                            className="py-2.5 px-3 rounded-xl text-xs font-bold border border-emerald-300 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all text-emerald-700 dark:text-emerald-400 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                        >
                            <SendOutlined className="text-emerald-600 text-sm" />
                            <span>Send via WhatsApp</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleExportCSV}
                            className="py-2.5 px-3 rounded-xl text-xs font-bold border border-slate-200 dark:border-neutral-800 bg-slate-50 dark:bg-black hover:bg-slate-100 dark:hover:bg-neutral-900 transition-all text-slate-700 dark:text-slate-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                        >
                            <DownloadOutlined className="text-purple-600 text-sm" />
                            <span>Export Tab (.CSV)</span>
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="py-2.5 px-3 rounded-xl text-xs font-bold border border-slate-200 dark:border-neutral-800 bg-slate-50 dark:bg-black hover:bg-slate-100 dark:hover:bg-neutral-900 transition-all text-slate-700 dark:text-slate-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                        >
                            <PrinterOutlined className="text-blue-600 text-sm" />
                            <span>Print / PDF</span>
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
